import type { StopSection } from "~/shared/schemas/shipment-details-schema.ts";

export type OrderedStop = {
	readonly stop: StopSection;
	readonly type: "pickup" | "delivery";
};

type MergeStopsInput = {
	readonly pickups: readonly StopSection[];
	readonly deliveries: readonly StopSection[];
};

/**
 * Parse a `scheduledDate` string into a numeric timestamp. Returns `null` for
 * `null`, `undefined`, and malformed strings (per data-model.md §2.3).
 */
function parseScheduledTimestamp(
	value: string | null | undefined,
): number | null {
	if (!value) {
		return null;
	}
	const ms = Date.parse(value);
	return Number.isNaN(ms) ? null : ms;
}

/**
 * Merge pickups and deliveries into a single time-ordered timeline.
 *
 * Sort key (per spec FR-006 + research.md Decision 4):
 *   1. ScheduledDate ASC; null/invalid dates moved after all valid dates.
 *   2. Tiebreak A: Pickup before Delivery.
 *   3. Tiebreak B: Sequence ASC.
 *
 * Within the null-date bucket: all Pickups (by Sequence ASC) precede all
 * Deliveries (by Sequence ASC). Stable when all keys tie — preserves input
 * order, which is the API's `Sequence` order within type.
 */
export function mergeStops(input: MergeStopsInput): readonly OrderedStop[] {
	const ordered: OrderedStop[] = [
		...input.pickups.map<OrderedStop>((stop) => ({ stop, type: "pickup" })),
		...input.deliveries.map<OrderedStop>((stop) => ({
			stop,
			type: "delivery",
		})),
	];

	ordered.sort((a, b) => {
		const aTs = parseScheduledTimestamp(a.stop.scheduledDate);
		const bTs = parseScheduledTimestamp(b.stop.scheduledDate);

		// Null/invalid dates are appended after all dated stops.
		if (aTs === null && bTs !== null) return 1;
		if (aTs !== null && bTs === null) return -1;

		if (aTs !== null && bTs !== null && aTs !== bTs) {
			return aTs - bTs;
		}

		// Tiebreak A: Pickup before Delivery (applies in both dated and null buckets).
		if (a.type !== b.type) {
			return a.type === "pickup" ? -1 : 1;
		}

		// Tiebreak B: Sequence ASC.
		return a.stop.sequence - b.stop.sequence;
	});

	return ordered;
}
